import type { App } from 'homey-api';

import { RouteManager } from '../../../RouteManager';
import type { CommandItem, CommandProviderArgs } from '../command-provider.types';
import { COMMAND_SECTIONS } from '../command-types.ts';

import type { AppProps, AppsStoreState } from '../../../store/apps/AppsStore';
import { AppUtils } from '../../../store/apps/AppUtils';
import { HomeyUtils } from '../../../store/HomeyUtils.ts';
import { selectedHomeyStore } from '../../../store/SelectedHomeyStore';

import { glyph } from '../../../theme/glyph';
import { theme } from '../../../theme/theme';

import { copyToClipboard, isMatch, normalize } from './provider-utils.ts';

const SYSTEM_GENERAL_SETTING_ID = 'system/general';
const settingsEntriesCache = new WeakMap<object, Map<string, SettingsEntry[]>>();

const APP_STATES = {
  STARTING: 'starting',
  RUNNING: 'running',
  STOPPING: 'stopping',
  STOPPED: 'stopped',
  CRASHED: 'crashed',
  BOOTING: 'booting',
};

type SettingsProviderApi = CommandProviderArgs['api'];
type SettingsProviderI18n = CommandProviderArgs['i18n'];

type SettingsEntry = {
  settingId: string;
  title: string;
  section: CommandItem['section'];
  subtitle?: string;
  icon?: unknown;
  backgroundColor?: string;
  keywords: string[];
  sortOrder: number;
  meta?: Record<string, unknown>;
};

function getAppStateLabel(appState: App['state'], i18n: SettingsProviderI18n): string {
  switch (appState) {
    case APP_STATES.STARTING:
      return i18n.messageFormatter('common.starting');
    case APP_STATES.RUNNING:
      return i18n.messageFormatter('common.running');
    case APP_STATES.STOPPING:
      return i18n.messageFormatter('common.stopping');
    case APP_STATES.STOPPED:
      return i18n.messageFormatter('common.stopped');
    case APP_STATES.CRASHED:
      return i18n.messageFormatter('common.crashed');
    case APP_STATES.BOOTING:
      return i18n.messageFormatter('common.booting');
    default:
      if (typeof appState !== 'string' || appState === '') {
        return '';
      }

      return appState.charAt(0).toUpperCase() + appState.slice(1);
  }
}

function isAppRestartDisabled(app: AppProps, isRestartAvailable: boolean): boolean {
  if (isRestartAvailable !== true) {
    return true;
  }

  switch (true) {
    case app.enabled !== true:
    case app.state === 'stopping':
    case app.state === 'starting':
      return true;
    case app.state === 'running':
    case app.state === 'crashed':
    case app.state === 'stopped':
      return false;
    default:
      return true;
  }
}

function isAppConfigureDisabled(app: AppProps): boolean {
  switch (true) {
    case app.enabled !== true:
    case app.state === 'stopping':
    case app.state === 'starting':
    case app.state === 'crashed':
    case app.state === 'stopped':
      return true;
    case app.state === 'running':
      return false;
    default:
      return true;
  }
}

function getSystemGeneralActionCommands({
  query,
  api,
  i18n,
}: {
  query: string;
  api: SettingsProviderApi;
  i18n: SettingsProviderI18n;
}): CommandItem[] {
  const normalizedQuery = normalize(query.trim());
  const generalTitle = i18n.messageFormatter('settings.system.general');
  const canRestart = api?.isCloud === false;

  const commands: CommandItem[] = [
    {
      key: `cmd:setting:${SYSTEM_GENERAL_SETTING_ID}:open`,
      kind: 'action',
      section: COMMAND_SECTIONS.SETTINGS_ACTIONS,
      sortOrder: 10,
      title: i18n.messageFormatter('commandPalette.actions.open'),
      subtitle: generalTitle,
      icon: glyph.core.squareArrowUpRight,
      keywords: ['open', 'general settings', 'system general'],
      getOpenInNewTabPath() {
        return RouteManager.getPathForSetting(SYSTEM_GENERAL_SETTING_ID);
      },
      onRun() {
        RouteManager.toSetting(SYSTEM_GENERAL_SETTING_ID);
      },
    },
    {
      key: `cmd:setting:${SYSTEM_GENERAL_SETTING_ID}:diagnostics-report`,
      kind: 'action',
      section: COMMAND_SECTIONS.SETTINGS_ACTIONS,
      sortOrder: 20,
      title: i18n.messageFormatter('settings.system.diagnosticsReportCreateTitle'),
      subtitle: generalTitle,
      icon: glyph.core.paper,
      keywords: ['diagnostics', 'diagnostics report', 'report', 'system report', 'journal'],
      getOpenInNewTabPath() {
        return RouteManager.getPathForSetting(`${SYSTEM_GENERAL_SETTING_ID}/report`);
      },
      onRun() {
        RouteManager.toSetting(`${SYSTEM_GENERAL_SETTING_ID}/report`);
      },
    },
    ...(canRestart
      ? [
          {
            key: `cmd:setting:${SYSTEM_GENERAL_SETTING_ID}:restart`,
            kind: 'action',
            section: COMMAND_SECTIONS.SETTINGS_ACTIONS,
            sortOrder: 30,
            title: i18n.messageFormatter('common.restart'),
            subtitle: generalTitle,
            icon: glyph.core.arrowDoubleCircularRotated,
            keywords: ['restart', 'reboot', 'system restart', 'homey restart'],
            async onRun() {
              await selectedHomeyStore.reboot();
            },
          },
        ]
      : []),
  ];

  if (normalizedQuery === '') {
    return commands;
  }

  return commands.filter((command) => {
    return (
      isMatch(command.title, normalizedQuery) ||
      isMatch(command.subtitle, normalizedQuery) ||
      command.keywords?.some((keyword) => {
        return isMatch(keyword, normalizedQuery);
      }) === true
    );
  });
}

function getSettingsEntries({
  api,
  employeeFlag,
  i18n,
  appsById,
}: {
  api: SettingsProviderApi;
  employeeFlag: boolean;
  i18n: SettingsProviderI18n;
  appsById: AppsStoreState['byId'];
}): SettingsEntry[] {
  const cacheKey = getSettingsEntriesCacheKey({ api, employeeFlag, i18n });
  let entriesByCacheKey = appsById != null ? settingsEntriesCache.get(appsById) : null;

  if (entriesByCacheKey == null) {
    entriesByCacheKey = new Map<string, SettingsEntry[]>();

    if (appsById != null) {
      settingsEntriesCache.set(appsById, entriesByCacheKey);
    }
  }

  const cachedEntries = entriesByCacheKey.get(cacheKey);

  if (cachedEntries != null) {
    return cachedEntries;
  }

  const systemLabel = i18n.messageFormatter('settings.system.label');
  const integrationsLabel = i18n.messageFormatter('settings.integrations.label');

  const entries = [
    {
      settingId: SYSTEM_GENERAL_SETTING_ID,
      title: i18n.messageFormatter('settings.system.general'),
      section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
      subtitle: systemLabel,
      icon: glyph.system.settings,
      backgroundColor: theme.color.system_general,
      keywords: ['settings', 'system', 'general'],
      meta: {
        contextType: 'setting',
        settingId: SYSTEM_GENERAL_SETTING_ID,
        settingTitle: i18n.messageFormatter('settings.system.general'),
        canEnterContextWithTab: true,
      },
    },
    ...(api?.isCloud !== true
      ? [
          {
            settingId: 'system/updates',
            title: i18n.messageFormatter('settings.system.updates'),
            section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
            subtitle: systemLabel,
            icon: glyph.system.updates,
            backgroundColor: theme.color.system_updates,
            keywords: ['settings', 'system', 'system updates', 'updates', 'update'],
          },
        ]
      : []),
    ...(HomeyUtils.getHasDeviceUpdates(api?.homey) === true
      ? [
          {
            settingId: 'system/device-updates',
            title: i18n.messageFormatter('settings.system.deviceUpdates'),
            section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
            subtitle: systemLabel,
            icon: glyph.system.ota,
            backgroundColor: theme.color.system_ota,
            keywords: ['settings', 'system', 'device updates', 'firmware', 'ota'],
          },
        ]
      : []),
    ...(api?.isCloud !== true && api?.homey.model !== 'shs' && api?.homey.model !== 'homey6q'
      ? [
          {
            settingId: 'system/led-ring',
            title: i18n.messageFormatter('settings.system.ledRing'),
            section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
            subtitle: systemLabel,
            icon: glyph.system.ledRing,
            backgroundColor: theme.color.system_ledring,
            keywords: ['settings', 'system', 'led ring'],
          },
        ]
      : []),
    {
      settingId: 'system/language',
      title: i18n.messageFormatter('settings.system.language'),
      section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
      subtitle: systemLabel,
      icon: glyph.system.language,
      backgroundColor: theme.color.system_language,
      keywords: ['settings', 'system', 'language'],
    },
    {
      settingId: 'system/location',
      title: i18n.messageFormatter('settings.system.location'),
      section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
      subtitle: systemLabel,
      icon: glyph.system.location,
      backgroundColor: theme.color.system_location,
      keywords: ['settings', 'system', 'location'],
    },
    {
      settingId: 'system/timeline',
      title: i18n.messageFormatter('settings.system.notifications'),
      section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
      subtitle: systemLabel,
      icon: glyph.system.timelineNotifications,
      backgroundColor: theme.color.system_timeline_notifications,
      keywords: ['settings', 'system', 'timeline', 'notifications'],
    },
    {
      settingId: 'system/family',
      title: i18n.messageFormatter('settings.system.users'),
      section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
      subtitle: systemLabel,
      icon: glyph.system.family,
      backgroundColor: theme.color.system_family,
      keywords: ['settings', 'system', 'family', 'guests', 'users'],
    },
    ...(api?.isCloud !== true && Number(api?.homey.platformVersion) >= 2
      ? [
          {
            settingId: 'system/api-keys',
            title: i18n.messageFormatter('settings.system.apiKeys'),
            section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
            subtitle: systemLabel,
            icon: glyph.system.apiKeys,
            backgroundColor: theme.color.system_api_keys,
            keywords: ['settings', 'system', 'api keys', 'token', 'pat'],
          },
        ]
      : []),
    ...(api?.isCloud !== true
      ? [
          {
            settingId: 'system/experiments',
            title: i18n.messageFormatter('settings.system.experiments'),
            section: COMMAND_SECTIONS.SETTINGS_SYSTEM,
            subtitle: systemLabel,
            icon: glyph.system.experiments,
            backgroundColor: theme.color.system_experiments,
            keywords: ['settings', 'system', 'experiments'],
          },
        ]
      : []),
    {
      settingId: 'integrations/alexa',
      title: i18n.messageFormatter('settings.integrations.alexa'),
      section: COMMAND_SECTIONS.SETTINGS_INTEGRATIONS,
      subtitle: integrationsLabel,
      icon: glyph.system.alexa,
      backgroundColor: theme.color.system_alexa,
      keywords: ['settings', 'integrations', 'alexa', 'amazon'],
    },
    {
      settingId: 'integrations/google-assistant',
      title: i18n.messageFormatter('settings.integrations.googleAssistant'),
      section: COMMAND_SECTIONS.SETTINGS_INTEGRATIONS,
      subtitle: integrationsLabel,
      icon: glyph.system.google,
      backgroundColor: theme.color.system_google,
      keywords: ['settings', 'integrations', 'google assistant'],
    },
    {
      settingId: 'integrations/siri-shortcuts',
      title: i18n.messageFormatter('settings.integrations.siriShortcuts'),
      section: COMMAND_SECTIONS.SETTINGS_INTEGRATIONS,
      subtitle: integrationsLabel,
      icon: glyph.system.siriShortcuts,
      backgroundColor: theme.color.system_siri_shortcuts,
      keywords: ['settings', 'integrations', 'siri shortcuts'],
    },
  ].map((entry, index) => {
    return {
      ...entry,
      sortOrder: index + 10,
    };
  });

  const appEntries = Object.entries(appsById ?? {})
    .map(([appId, app]) => {
      const appState = app.state ?? '';
      const appStateLabel = getAppStateLabel(appState, i18n);

      return {
        settingId: `apps/${appId}`,
        title: app.name ?? appId,
        section: COMMAND_SECTIONS.SETTINGS_APPS,
        subtitle: appStateLabel,
        icon: app.iconObj == null ? glyph.system.settings : null,
        keywords: ['settings', 'apps', appId, app.name, appState, appStateLabel].filter(Boolean),
        meta: {
          entityType: 'app',
          entityId: appId,
          canEnterContextWithTab: true,
          appIconObj: app.iconObj ?? null,
          appIconBackgroundColor: AppUtils.getBackgroundColor(app),
        },
      };
    })
    .filter((entry): entry is NonNullable<typeof entry> => entry != null)
    .sort((a, b) => {
      return (
        i18n.collator.compare(a.title, b.title) ?? String(a.title).localeCompare(String(b.title))
      );
    })
    .map((entry, index) => {
      return {
        ...entry,
        sortOrder: index + 10,
      };
    });

  const settingsEntries = [...entries, ...appEntries];

  entriesByCacheKey.set(cacheKey, settingsEntries);

  return settingsEntries;
}

function getSettingsEntriesCacheKey({
  api,
  employeeFlag,
  i18n,
}: {
  api: SettingsProviderApi;
  employeeFlag: boolean;
  i18n: SettingsProviderI18n;
}): string {
  const localeCode = Object.keys(i18n.stringLocale ?? {})[0] ?? 'en';

  return JSON.stringify({
    localeCode,
    employeeFlag,
    isCloud: api?.isCloud === true,
    model: api?.homey?.model ?? '',
    platformVersion: Number(api?.homey?.platformVersion ?? 0),
    softwareVersion: api?.homey?.softwareVersion ?? '',
  });
}

function getAppContextCommands({
  activeContext,
  query,
  appsById,
  api,
  i18n,
}: {
  activeContext: CommandProviderArgs['activeContext'];
  query: string;
  appsById: AppsStoreState['byId'];
  api: SettingsProviderApi;
  i18n: SettingsProviderI18n;
}): CommandItem[] {
  if (activeContext?.type !== 'app') {
    return [];
  }

  const app = activeContext.appId ? appsById?.[activeContext.appId] : null;

  if (app == null || api == null) {
    return [];
  }

  const normalizedQuery = normalize(query.trim());
  const isRestartAvailable = api.isCloud !== true && api.apps != null;
  const restartDisabled = isAppRestartDisabled(app, isRestartAvailable);
  const isConfigureAvailable = app.settings === true;
  const configureDisabled = isAppConfigureDisabled(app);
  const canToggleAppEnabled = api.isCloud !== true && api.apps != null;
  const appIsEnabled = app?.enabled === true;
  const restartSubtitle =
    restartDisabled === true
      ? i18n.messageFormatter('settings.app.restartNotAvailable')
      : (app.name ?? app.id);
  const configureSubtitle =
    configureDisabled === true
      ? i18n.messageFormatter('commandPalette.actions.appSettingsUnavailable')
      : (app.name ?? app.id);

  const commands = [
    {
      key: `cmd:app:${app.id}:open`,
      kind: 'action',
      section: COMMAND_SECTIONS.CONTEXT_NAVIGATE,
      sortOrder: 10,
      title: i18n.messageFormatter('commandPalette.actions.open'),
      subtitle: app.name ?? app.id,
      icon: glyph.core.squareArrowUpRight,
      keywords: ['open', 'app page', 'go to app'],
      getOpenInNewTabPath() {
        return RouteManager.getPathForSetting(`apps/${app.id}`);
      },
      onRun() {
        RouteManager.toSetting(`apps/${app.id}`);
      },
    },
    ...(isConfigureAvailable
      ? [
          {
            key: `cmd:app:${app.id}:configure`,
            kind: 'action',
            section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
            sortOrder: 10,
            title: i18n.messageFormatter('settings.app.configure'),
            subtitle: configureSubtitle,
            icon: glyph.core.sliderHorizontal,
            keywords: ['configure', 'app settings', 'settings', 'app configure'],
            isDisabled: configureDisabled,
            getOpenInNewTabPath() {
              return RouteManager.getPathForSetting(`apps/${app.id}/settings`);
            },
            onRun() {
              RouteManager.toSetting(`apps/${app.id}/settings`);
            },
          },
        ]
      : []),
    ...(canToggleAppEnabled
      ? [
          {
            key: `cmd:app:${app.id}:toggle-enabled`,
            kind: 'action',
            section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
            sortOrder: 20,
            title: i18n.messageFormatter(
              appIsEnabled === true ? 'common.disable' : 'common.enable',
            ),
            subtitle: app.name ?? app.id,
            icon: appIsEnabled === true ? glyph.core.cross : glyph.core.checkmark,
            keywords: ['enable', 'disable', 'app state', 'toggle app'],
            keepOpenOnShiftEnter: true,
            async onRun() {
              if (appIsEnabled === true) {
                await api.apps.disableApp?.({ id: app.id });
                return;
              }

              await api.apps.enableApp?.({ id: app.id });
            },
          },
        ]
      : []),
    ...(isRestartAvailable
      ? [
          {
            key: `cmd:app:${app.id}:restart`,
            kind: 'action',
            section: COMMAND_SECTIONS.CONTEXT_ACTIONS,
            sortOrder: 30,
            title: i18n.messageFormatter('common.restart'),
            subtitle: restartSubtitle,
            icon: glyph.core.arrowDoubleCircularRotated,
            keywords: ['restart', 'app restart'],
            isDisabled: restartDisabled,
            async onRun() {
              await api.apps?.restartApp?.({ id: app.id });
            },
          },
        ]
      : []),
    {
      key: `cmd:app:${app.id}:copy-id`,
      kind: 'action',
      section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
      sortOrder: 10,
      title: i18n.messageFormatter('commandPalette.actions.copyId'),
      subtitle: app.name ?? app.id,
      icon: glyph.core.squareOnSquare,
      keywords: ['copy', 'app id', 'id'],
      onRun() {
        return copyToClipboard({ value: app.id, icon: glyph.core.squareOnSquare });
      },
    },
    {
      key: `cmd:app:${app.id}:copy-json`,
      kind: 'action',
      section: COMMAND_SECTIONS.CONTEXT_DEVELOPER,
      sortOrder: 20,
      title: i18n.messageFormatter('commandPalette.actions.copyJson'),
      subtitle: app.name ?? app.id,
      icon: glyph.core.squareOnSquare,
      keywords: ['copy', 'json', 'app json'],
      onRun() {
        return copyToClipboard({
          value: JSON.stringify(app, null, 2),
          icon: glyph.core.squareOnSquare,
        });
      },
    },
  ];

  return commands.filter((command) => {
    return (
      isMatch(command.title, normalizedQuery) ||
      isMatch(command.subtitle, normalizedQuery) ||
      command.keywords.some((keyword) => {
        return isMatch(keyword, normalizedQuery);
      })
    );
  });
}

export function getSettingsCommands(args: CommandProviderArgs): CommandItem[] {
  const { query, activeContext, api, employeeFlag, i18n, appsById } = args;

  if (activeContext?.type === 'device' || activeContext?.type === 'flow') {
    return [];
  }

  if (activeContext?.type === 'setting') {
    if (activeContext.settingId !== SYSTEM_GENERAL_SETTING_ID) {
      return [];
    }

    return getSystemGeneralActionCommands({
      query,
      api,
      i18n,
    });
  }

  if (activeContext?.type === 'app') {
    return getAppContextCommands({
      activeContext,
      query,
      appsById,
      api,
      i18n,
    });
  }

  if (activeContext?.type === 'scope' && activeContext.scopeType !== 'settings') {
    return [];
  }

  const isSettingsScopeContext =
    activeContext?.type === 'scope' && activeContext.scopeType === 'settings';
  const normalizedQuery = normalize(query.trim());

  if (isSettingsScopeContext !== true && normalizedQuery === '') {
    return [];
  }

  const settingsEntries = getSettingsEntries({ api, employeeFlag, i18n, appsById });
  const globalSystemGeneralActionCommands =
    activeContext == null && normalizedQuery !== ''
      ? getSystemGeneralActionCommands({
          query,
          api,
          i18n,
        }).filter((command) => {
          return command.key !== `cmd:setting:${SYSTEM_GENERAL_SETTING_ID}:open`;
        })
      : [];

  const scopeCommands: CommandItem[] = isSettingsScopeContext
    ? [
        {
          key: 'cmd:scope:settings:open',
          kind: 'action',
          section: COMMAND_SECTIONS.SETTINGS_ACTIONS,
          sortOrder: 10,
          title: i18n.messageFormatter('commandPalette.actions.open'),
          subtitle: i18n.messageFormatter('commandPalette.actions.settingsPage'),
          icon: glyph.core.squareArrowUpRight,
          keywords: ['open', 'settings', 'go to settings'],
          getOpenInNewTabPath() {
            return RouteManager.getPathForSettings();
          },
          onRun() {
            RouteManager.toSettings();
          },
        },
      ]
    : [];

  const settingCommands: CommandItem[] = [];

  for (const entry of settingsEntries) {
    if (
      isSettingsScopeContext !== true &&
      isMatch(entry.title, normalizedQuery) !== true &&
      isMatch(entry.subtitle, normalizedQuery) !== true &&
      isMatch(entry.settingId, normalizedQuery) !== true &&
      entry.keywords.some((keyword) => {
        return isMatch(keyword, normalizedQuery);
      }) !== true
    ) {
      continue;
    }

    const entryMeta =
      entry.meta != null || entry.backgroundColor != null
        ? {
            ...(entry.meta ?? {}),
            iconBackgroundColor: entry.backgroundColor ?? entry.meta?.iconBackgroundColor,
          }
        : undefined;

    settingCommands.push({
      key: `cmd:setting:${entry.settingId}`,
      kind: 'command',
      section: entry.section,
      sortOrder: entry.sortOrder,
      title: entry.title,
      subtitle: entry.subtitle,
      icon: entry.icon,
      keywords: entry.keywords,
      meta: entryMeta,
      getOpenInNewTabPath() {
        return RouteManager.getPathForSetting(entry.settingId);
      },
      onRun() {
        RouteManager.toSetting(entry.settingId);
      },
    });
  }

  return [...scopeCommands, ...settingCommands, ...globalSystemGeneralActionCommands];
}
